import {
  CancelablePromise,
  CategoriesService,
  Category,
  Contact,
  ContactsService,
  Horse,
  HorsesService,
  Product,
  ProductsService,
  Stable,
  StablesService,
} from 'openapi';

export type PaginatedList<T> = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Array<T>;
};

/**
 * Uniquely identify a cached entry. Username and organization allow us to stay
 * in scope. The name field sets a name for the data being stored. I.e. 'horses'.
 */
export interface CacheKey {
  userUid: string;
  organizationUid: string;
  name: string;
}

interface PredefinedApiListRequest<T> {
  promiseCallback: () => CancelablePromise<PaginatedList<T>> | CancelablePromise<T[]>;
  cacheKey?: CacheKey;
}

/**
 * Predefined request for all contacts.
 */
export const predefinedApiRequestContacts = (
  organizationUid: string,
  generateCacheKey: (name: string) => CacheKey,
): PredefinedApiListRequest<Contact> => {
  return {
    promiseCallback: () => {
      return ContactsService.contactsList({
        organisationUid: organizationUid,
        o: 'lookup',
      });
    },
    cacheKey: generateCacheKey('all-contacts'),
  };
};

/**
 * Predefined request for all non-hidden organization contacts.
 */
export const predefinedApiRequestActiveContacts = (
  organizationUid: string,
  generateCacheKey: (name: string) => CacheKey,
): PredefinedApiListRequest<Contact> => {
  return {
    promiseCallback: () => {
      return ContactsService.contactsList({
        organisationUid: organizationUid,
        hidden: false,
        o: 'lookup',
      });
    },
    cacheKey: generateCacheKey('active-contacts'),
  };
};

/**
 * Predefined request for all non-hidden organization contacts.
 */
export const predefinedApiRequestActiveContactsNotAUser = (
  organisationUid: string,
  generateCacheKey: (name: string) => CacheKey,
): PredefinedApiListRequest<Contact> => {
  return {
    promiseCallback: () => {
      return ContactsService.contactsList({
        organisationUid,
        hidden: false,
        isUser: false,
        o: 'lookup',
      });
    },
    cacheKey: generateCacheKey('active-contacts-not-a-user'),
  };
};

/**
 * Predefined request for all non-hidden organization horses.
 */
export const predefinedApiRequestActiveOrganizationHorses = (
  organizationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Horse> => {
  return {
    promiseCallback: () => {
      return HorsesService.horsesList({
        organisationUid: organizationUid,
        hidden: false,
        hasStable: true,
        o: 'name',
      });
    },
    cacheKey: generateCacheKey ? generateCacheKey('active-organization-horses') : undefined,
  };
};

/**
 * Predefined request for all organization horses. Including the hidden/archived onces.
 */
export const predefinedApiRequestOrganizationHorses = (
  organizationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Horse> => {
  return {
    promiseCallback: () => {
      return HorsesService.horsesList({
        organisationUid: organizationUid,
        hasStable: true,
        o: 'name',
      });
    },
    cacheKey: generateCacheKey ? generateCacheKey('organization-horses') : undefined,
  };
};

/**
 * Predefined request for all active horses
 */
export const predefinedApiRequestAllActiveHorses = (
  organizationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Horse> => {
  return {
    promiseCallback: () => {
      return HorsesService.horsesList({
        organisationUid: organizationUid,
        hidden: false,
        o: 'name',
      });
    },
    cacheKey: generateCacheKey ? generateCacheKey('all-active-horses') : undefined,
  };
};

/**
 * Predefined request for all horses
 */
export const predefinedApiRequestAllHorses = (
  organizationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Horse> => {
  return {
    promiseCallback: () => {
      return HorsesService.horsesList({
        organisationUid: organizationUid,
        o: 'name',
      });
    },
    cacheKey: generateCacheKey ? generateCacheKey('all-horses') : undefined,
  };
};

/**
 * Predefined request for the product horses
 */
export const predefinedApiRequestProductHorses = (
  organizationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Horse> => {
  return {
    promiseCallback: () => {
      return HorsesService.horsesList({
        organisationUid: organizationUid,
        hidden: false,
        isProduct: true,
        o: 'name',
      });
    },
    cacheKey: generateCacheKey ? generateCacheKey('product-horses') : undefined,
  };
};

/**
 * Predefined request for the categories
 * @param organisationUid
 * @param generateCacheKey
 * @returns
 */
export const predefinedApiRequestCategories = (
  organisationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Category> => {
  return {
    promiseCallback: () =>
      CategoriesService.categoriesList({
        organisationUid,
        o: 'p_s_type,name',
      }),
    cacheKey: generateCacheKey ? generateCacheKey('categories') : undefined,
  };
};

/**
 * Predefined request for the products
 *
 * @param organisationUid
 * @param generateCacheKey
 * @returns
 */
export const predefinedApiRequestProducts = (
  organisationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Product> => {
  return {
    promiseCallback: () =>
      ProductsService.productsList({
        organisationUid,
      }),
    cacheKey: generateCacheKey ? generateCacheKey('products') : undefined,
  };
};

/**
 * Predefined request for the stables
 *
 * @param organisationUid
 * @param generateCacheKey
 * @returns
 */
export const predefinedApiRequestStables = (
  organisationUid: string,
  generateCacheKey?: (name: string) => CacheKey,
): PredefinedApiListRequest<Stable> => {
  return {
    promiseCallback: () =>
      StablesService.stablesList({
        organisationUid,
      }),
    cacheKey: generateCacheKey ? generateCacheKey('stables') : undefined,
  };
};
